import type { Connection, Location } from '../api';
import manifest from '../data/digitalMapLayout.json';
import { DEFAULT_MAP_SECTION_ORDER, MapSection, normalizeMapSectionOrder } from '../utils/mapSectionOrder';

export type Point = { x: number; y: number };
export type Camera = Point & { scale: number };
export type Size = { width: number; height: number };
export type Bounds = Point & Size;

const scaledToken = (size: number) => Math.round(size * manifest.entityScale * 1000) / 1000;

/** Map-only dimensions; inspectors and Classic keep their own presentation sizes. */
export const DIGITAL_TOKENS = Object.freeze({
  scale: manifest.entityScale, gate: scaledToken(62), investigator: scaledToken(55.8 * .9),
  monster: scaledToken(52), destination: scaledToken(18),
  clue: scaledToken(22), extra: scaledToken(22), rift: scaledToken(24),
  whisperHeight: scaledToken(54), whisperWidth: scaledToken(56.7),
  encounter: scaledToken(18), vortex: scaledToken(42),
  activity: scaledToken(22), activityStep: scaledToken(11),
  clueStep: scaledToken(5.5), extraStep: scaledToken(26),
});

/** Strict consecutive slots: map content never displaces an individual ring action. */
export function movementRingPositions(center: Point, layout: { rectangular: boolean; frameWidth: number; frameHeight: number; radius: number }, count: number, buttonSize = 28): Point[] {
  const slots = Math.max(12, count);
  const step = 2 * Math.PI / slots;
  const frameRadius = layout.rectangular ? Math.hypot(layout.frameWidth / 2, layout.frameHeight / 2) : layout.radius;
  const radius = Math.max(frameRadius + buttonSize / 2 + 8, (buttonSize + 6) / (2 * Math.sin(step / 2)));
  return Array.from({ length: count }, (_, index) => {
    const angle = Math.PI / 4 + index * step;
    return { x: center.x + radius * Math.cos(angle) - buttonSize / 2, y: center.y + radius * Math.sin(angle) - buttonSize / 2 };
  });
}
export type MapPosition = Point & { renderScale?: number; city: string; kind: string; frameSize?: Size; extrasSide?: string; label?: { width?: number; offsetX?: number; offsetY?: number } };
export type DigitalMapCity = {
  name: string; background: string; sourceScale?: number; bounds: Bounds; section: Bounds; title: Point; track?: Bounds;
};
export type DigitalMapLayout = {
  regions?: { name: string; x: number; y: number; scale: number; layout: DigitalMapLayout }[];
  backgrounds?: { name: string; background: string; bounds: Bounds }[];
  otherWorldsBelowMap?: boolean;
  renderRegion?: boolean;
  board: Size; positions: Record<string, MapPosition>; cities: DigitalMapCity[];
  vortexes: (Point & { source: string; city: string })[]; initialBounds: Bounds; signature: string; tokens: typeof DIGITAL_TOKENS;
};
export const WORLD_NAMES = Object.keys(manifest.positions).filter(name =>
  (manifest.positions as Record<string, MapPosition>)[name].kind === 'world');

/** Resolve local artwork coordinates into one compact scene; never mutate shared anchors. */
export function buildDigitalMapLayout(flags: ExpansionFlags = {}, locations: Location[] = [], otherWorldsBelowMap = false, monsterScale = 1, sectionOrder: readonly MapSection[] = DEFAULT_MAP_SECTION_ORDER): DigitalMapLayout {
  const width = manifest.width, gutter = 64, padding = 32, headingHeight = 64, cityGap = 72;
  const result: DigitalMapLayout = { board: { width: width + gutter, height: 0 }, positions: {}, cities: [], vortexes: [],
    initialBounds: { x: 0, y: 0, width, height: 0 }, signature: '',
    tokens: { ...DIGITAL_TOKENS, monster: DIGITAL_TOKENS.monster * monsterScale } };
  let top = 0;
  const order = normalizeMapSectionOrder(sectionOrder);
  const enabledCities = manifest.cities.filter(city => cityEnabled(city.name, flags))
    .sort((a, b) => order.indexOf(a.name as MapSection) - order.indexOf(b.name as MapSection));
  for (const city of enabledCities) {
    const scale = width / city.width;
    const bounds = { x: gutter, y: top + headingHeight, width, height: city.height * scale };
    for (const [name, anchor] of Object.entries(manifest.positions)) {
      if (anchor.city === city.name) {
        // Calibration is normalized to city width, independently of source-image anchor units.
        const normalized = (city.sizing.frames as Record<string, Size | undefined>)[name]
          ?? { width: city.sizing.locationDiameter, height: city.sizing.locationDiameter };
        result.positions[name] = {
          ...anchor, x: bounds.x + anchor.x * scale, y: bounds.y + anchor.y * scale,
          frameSize: anchor.kind === 'world'
            ? { width: city.sizing.locationDiameter * width * manifest.worldAspectRatio, height: city.sizing.locationDiameter * width * manifest.worldHeightMultiplier }
            : { width: normalized.width * width, height: normalized.height * width },
        };
      }
    }
    result.vortexes.push(...manifest.vortexes.filter(vortex => vortex.city === city.name)
      .map(vortex => {
        const source = result.positions[vortex.source], frame = nodeLayout(vortex.source, '', false, false, result);
        const above = 'placement' in vortex && vortex.placement === 'above';
        const dx = above ? 0 : bounds.x + vortex.x * scale - source.x, dy = above ? -1 : bounds.y + vortex.y * scale - source.y;
        const distance = Math.hypot(dx, dy), direction = { x: dx / distance, y: dy / distance };
        const edge = frame.rectangular ? Math.min((frame.frameWidth / 2 + 2.5) / Math.abs(direction.x),
          (frame.frameHeight / 2 + 2.5) / Math.abs(direction.y)) : frame.radius + 7.5;
        // Keep the authored direction and enough room for the sink and its movement arrow.
        const length = Math.max(above ? 0 : distance, edge + DIGITAL_TOKENS.vortex / 2 + 39);
        return { ...vortex, x: source.x + direction.x * length, y: source.y + direction.y * length };
      }));
    // Panel bounds use the common 1774-unit image width so tokens keep their existing sizes.
    const track = city.track ? { x: bounds.x + ('centerInCity' in city.track && city.track.centerInCity
      ? (city.stripX * scale - city.track.width) / 2 : city.track.x), y: bounds.y + city.track.y,
      width: city.track.width, height: city.track.height } : undefined;
    if (city.name === 'Arkham') {
      // Size preferences change pieces and title clearance, never the authored row anchors.
      const names = ['Outskirts', 'Sky', 'Lost in Time and Space'];
      const belowCenter = Math.max(...names.map(name => {
        const location = locations.find(location => location.name === name);
        const box = nodeBounds(name, location?.specialEncounterText,
          name !== 'Lost in Time and Space' && !!location?.monsters?.length, false, { ...result, tokens: DIGITAL_TOKENS });
        return box.y + box.height - result.positions[name].y;
      }));
      const rowY = Math.min(result.positions.Outskirts.y, bounds.y + bounds.height - padding - belowCenter);
      names.forEach(name => { result.positions[name].y = rowY; });
    }
    const section = { x: 0, y: top, width: width + gutter, height: headingHeight + bounds.height + padding };
    result.cities.push({ name: city.name, background: city.background, bounds, section,
      title: { x: bounds.x + city.stripX * scale / 2, y: top + 34 }, track });
    if (city.name === 'Arkham') result.initialBounds = { ...section };
    top += section.height + cityGap;
  }
  result.board.height = top - cityGap;
  result.signature = result.cities.map(city => city.name).join('-');
  return arrangeSections(otherWorldsBelowMap ? detachOtherWorlds(result, locations) : result, order);
}

/** Move complete sections together, leaving each render region's local coordinates intact. */
function arrangeSections(layout: DigitalMapLayout, order: MapSection[]): DigitalMapLayout {
  layout.cities.sort((a, b) => order.indexOf(a.name as MapSection) - order.indexOf(b.name as MapSection));
  let top = 0;
  for (const city of layout.cities) {
    // The Innsmouth bus route enters through the heading above its background.
    if (city.name === 'Innsmouth') {
      const source = manifest.cities.find(item => item.name === city.name)!;
      const scale = city.sourceScale ?? city.bounds.width / source.width;
      top = Math.max(top, 8 + 96 * scale - (city.bounds.y - city.section.y));
    }
    const dy = top - city.section.y;
    city.bounds = { ...city.bounds, y: city.bounds.y + dy };
    city.section = { ...city.section, y: top };
    city.title = { ...city.title, y: city.title.y + dy };
    if (city.track) city.track = { ...city.track, y: city.track.y + dy };
    for (const [name, point] of Object.entries(layout.positions)) {
      if (city.name === 'Other Worlds' ? point.kind === 'world' : point.city === city.name && (!layout.otherWorldsBelowMap || point.kind !== 'world')) {
        layout.positions[name] = { ...point, y: point.y + dy };
      }
    }
    layout.vortexes = layout.vortexes.map(point => point.city === city.name ? { ...point, y: point.y + dy } : point);
    const region = layout.regions?.find(item => item.name === city.name);
    if (region) region.y += dy;
    const background = layout.backgrounds?.find(item => item.name === city.name);
    if (background) background.bounds = { ...background.bounds, y: background.bounds.y + dy };
    if (city.name === 'Arkham') layout.initialBounds = { ...city.section };
    top += city.section.height + 72;
  }
  layout.board = { ...layout.board, height: top - 72 };
  // Keep existing default Digital camera keys while distinguishing every effective arrangement.
  layout.signature = layout.cities.filter(city => city.name !== 'Other Worlds').map(city => city.name).join('-')
    + (layout.otherWorldsBelowMap ? '-worlds-below' : '');
  if (layout.otherWorldsBelowMap && layout.cities[layout.cities.length - 1]?.name !== 'Other Worlds') {
    layout.signature += ':' + layout.cities.map(city => city.name).join('-');
  }
  return layout;
}

/** Each render region uses the original local geometry, keeping all SVG/HTML dimensions proportional. */
function detachOtherWorlds(original: DigitalMapLayout, locations: Location[]): DigitalMapLayout {
  const width = manifest.width, gutter = 64, heading = 64, gap = 72, padding = 32;
  const result: DigitalMapLayout = { ...original, positions: {}, cities: [], vortexes: [], regions: [], backgrounds: [],
    otherWorldsBelowMap: true, signature: original.signature + '-worlds-below' };
  let top = 0;
  for (const city of original.cities) {
    const source = manifest.cities.find(item => item.name === city.name)!;
    const cropWidth = city.bounds.width * source.stripX / source.width;
    const scale = width / cropWidth;
    const dy = city.bounds.y - heading;
    const local: DigitalMapLayout = { ...original, regions: undefined, backgrounds: undefined,
      board: { width: gutter + cropWidth, height: heading + city.bounds.height + padding },
      positions: {}, cities: [{ ...city, bounds: { ...city.bounds, y: heading } }], vortexes: [], renderRegion: true, initialBounds: original.initialBounds };
    for (const [name, position] of Object.entries(original.positions)) {
      if (position.city !== city.name || position.kind === 'world') continue;
      local.positions[name] = { ...position, y: position.y - dy };
    }
    // Two edge locations slightly crossed the old strip boundary; keep their complete frames/titles inside the crop.
    for (const [name, position] of Object.entries(local.positions)) {
      const location = locations.find(item => item.name === name);
      const box = nodeBounds(name, location?.specialEncounterText, !!location?.monsters?.length, false, local);
      position.x += box.x < 8 ? 8 - box.x : Math.min(0, local.board.width - 8 - box.x - box.width);
    }
    const x = gutter - gutter * scale, y = top + heading - heading * scale;
    const bounds = { x: gutter, y: top + heading, width, height: city.bounds.height * scale };
    const track = city.track && (city.name === 'Innsmouth'
      ? { x: gutter, y: bounds.y + bounds.height + 24, width, height: 170 * scale }
      : { x: x + city.track.x * scale, y: y + (city.track.y - dy) * scale,
        width: city.track.width * scale, height: city.track.height * scale });
    const bottom = city.name === 'Innsmouth' && track ? track.y + track.height : bounds.y + bounds.height;
    const section = { x: 0, y: top, width: width + gutter, height: bottom - top + padding * scale };
    const resolvedCity = { ...city, bounds, section, track, sourceScale: width / source.stripX,
      title: { x: gutter + width / 2, y: top + 34 } };
    result.cities.push(resolvedCity);
    for (const [name, position] of Object.entries(local.positions)) {
      result.positions[name] = { ...position, x: x + position.x * scale, y: y + position.y * scale,
        renderScale: scale, frameSize: position.frameSize && { width: position.frameSize.width * scale, height: position.frameSize.height * scale } };
    }
    local.vortexes = original.vortexes.filter(v => v.city === city.name).map(v => ({ ...v, y: v.y - dy }));
    result.vortexes.push(...local.vortexes.map(v => ({ ...v, x: x + v.x * scale, y: y + v.y * scale })));
    // Titles are rendered once in the global scene, independently of local scaling.
    result.regions!.push({ name: city.name, x, y, scale, layout: local });
    result.backgrounds!.push({ name: city.name, background: city.background.replace(/\.jpg$/, '-city.png'), bounds });
    if (city.name === 'Arkham') result.initialBounds = section;
    top += section.height + gap;
  }
  const worldNames = original.cities.flatMap(city => WORLD_NAMES.filter(name => original.positions[name]?.city === city.name)
    .sort((a, b) => original.positions[a].y - original.positions[b].y));
  const worldLayout: DigitalMapLayout = { ...original, positions: {}, cities: [], vortexes: [], regions: undefined,
    board: { width: width + gutter, height: 0 }, renderRegion: true };
  const columns = 4, cellWidth = width / columns;
  let rowTop = top + heading;
  for (let row = 0; row * columns < worldNames.length; row++) {
    const names = worldNames.slice(row * columns, (row + 1) * columns);
    let above = 0, below = 0;
    names.forEach((name, column) => {
      const position = original.positions[name];
      worldLayout.positions[name] = { ...position, x: gutter + (column + .5) * cellWidth, y: 0 };
      const text = Array.from(new Set(locations.filter(l => worldName(l.name) === name).map(l => l.specialEncounterText?.trim()).filter(Boolean))).join('\n');
      const box = nodeBounds(name, text, false, false, worldLayout);
      above = Math.max(above, -box.y, DIGITAL_TOKENS.investigator / 2);
      below = Math.max(below, box.y + box.height + DIGITAL_TOKENS.destination + 32);
    });
    const centerY = rowTop + above + padding;
    names.forEach(name => { worldLayout.positions[name].y = centerY; });
    rowTop += above + below + padding * 2;
  }
  if (worldNames.length) {
    result.regions!.push({ name: 'Other Worlds', x: 0, y: 0, scale: 1, layout: worldLayout });
    Object.assign(result.positions, worldLayout.positions);
    result.backgrounds!.push({ name: 'Other Worlds', background: '/map/other-worlds-background.png',
      bounds: { x: gutter, y: top + heading, width, height: rowTop - top - heading + padding } });
    result.cities.push({ name: 'Other Worlds', background: '/map/other-worlds-background.png',
      bounds: { x: gutter, y: top + heading, width, height: rowTop - top - heading + padding },
      section: { x: 0, y: top, width: width + gutter, height: rowTop - top + padding },
      title: { x: gutter + width / 2, y: top + 34 } });
  }
  result.board = { width: width + gutter, height: worldNames.length ? rowTop + padding : top - gap };
  worldLayout.board = result.board;
  return result;
}

export const digitalMapCameraKey = (gameId: string, layout: DigitalMapLayout) =>
  `arkhamDigitalMapCamera:v4:${gameId}:${layout.signature}`;

// Colors are sampled/interpreted from the neighborhood frames on the original boards.
const DISTRICT_COLORS: Record<string, string> = {
  Northside: '#c76531', downtown: '#e4dfd4', Easttown: '#9680aa',
  'Merchant District': '#8c9b61', rivertown: '#8057ac', 'Miskatonic University': '#ddc341',
  'French Hill': '#4e8bc2', Uptown: '#bc4944', Southside: '#92715b',
  'Backwoods Country': '#bc6546', 'Village Commons': '#adb55a', 'Blasted Heath': '#598cb0',
  'Sentinel Hill': '#a4a29b', 'Central Hill': '#5989b6', Harborside: '#d9bc55',
  'South Shore': '#b35b50', 'Kingsport Head': '#e4dfd4', 'Factory District': '#a4a29b',
  'Church Green': '#c9a653', 'Innsmouth Shore': '#6e9b80', Special: '#b8a58a',
};
export const districtColor = (district: string) => DISTRICT_COLORS[district]
  ?? DISTRICT_COLORS[Object.keys(DISTRICT_COLORS).find(key => key.toLowerCase() === district.toLowerCase()) ?? '']
  ?? '#b8a58a';

export const artworkPath = (name: string, world = false) =>
  `/${world ? 'OtherWorlds' : 'Locations'}/rework/small/${name.replace(/[\s.'"-]/g, '')}.jpg`;
export const worldName = (name: string) => name.replace(/ [12]$/, '');
export const displayName = (name: string) => name === 'Rlyeh' ? "R’lyeh"
  : name === 'DownTown' ? 'Downtown' : name === 'RiverTown' ? 'Rivertown' : name.replace(/([a-z])([A-Z])/g, '$1 $2');
export function labelLines(name: string, width?: number, fontSize = 12): string[] {
  if (name === 'Jail Cell') return ['Jail', 'Cell'];
  if (name === 'Innsmouth Jail Cell') return ['Innsmouth', 'Jail Cell'];
  const lines: string[] = [];
  displayName(name).split(' ').forEach(word => {
    const candidate = `${lines[lines.length - 1]} ${word}`;
    const fits = width === undefined ? candidate.length <= 22
      : Array.from(candidate).reduce((sum, char) => sum + characterWidth(char) * fontSize / 11, 0) <= width - 16;
    if (lines.length && fits) lines[lines.length - 1] += ` ${word}`;
    else lines.push(word);
  });
  return lines;
}

export type ExpansionFlags = { includeDunwichCards?: boolean; includeKingsportCards?: boolean; includeInnsmouthInCurrentGame?: boolean };
export const cityEnabled = (city: string | undefined, flags: ExpansionFlags) =>
  city === 'Dunwich' ? !!flags.includeDunwichCards : city === 'Kingsport' ? !!flags.includeKingsportCards
    : city === 'Innsmouth' ? !!flags.includeInnsmouthInCurrentGame : true;

export const visibleLocations = (locations: Location[], flags: ExpansionFlags) => locations.filter(location => {
  const position = (manifest.positions as Record<string, MapPosition>)[location.type === 'OtherWorld' ? worldName(location.name) : location.name];
  return !!position && cityEnabled(position.city, flags) && cityEnabled(location.city, flags);
});

export const minScale = (size: Size, board: Size = referenceLayout.board) => size.width / board.width;
export const maxScale = (size: Size, board: Size = referenceLayout.board) => Math.max(minScale(size, board), 4 * fitScale(size, board), 14 / 11);
export const fitScale = (size: Size, board: Size = referenceLayout.board) => Math.min(size.width / board.width, size.height / board.height);
export const clampScale = (scale: number, size: Size, board: Size = referenceLayout.board) =>
  Math.max(minScale(size, board), Math.min(maxScale(size, board), scale));
export function clampCamera(camera: Camera, size: Size, board: Size = referenceLayout.board, topInset = 0): Camera {
  const scale = clampScale(camera.scale, size, board);
  const axis = (offset: number, board: number, viewport: number) => board * scale <= viewport
    ? (viewport - board * scale) / 2 : Math.max(viewport - board * scale, Math.min(0, offset));
  const inset = Math.max(0, Math.min(topInset, size.height - 1));
  const y = board.height * scale <= size.height - inset
    ? inset + (size.height - inset - board.height * scale) / 2
    : Math.max(size.height - board.height * scale, Math.min(inset, camera.y));
  return { scale, x: axis(camera.x, board.width, size.width), y };
}
export const frameBounds = (bounds: Bounds, size: Size, board: Size = referenceLayout.board): Camera => {
  const scale = clampScale(Math.min(size.width / bounds.width, size.height / bounds.height) * .92, size, board);
  return clampCamera({ scale, x: size.width / 2 - (bounds.x + bounds.width / 2) * scale,
    y: size.height / 2 - (bounds.y + bounds.height / 2) * scale }, size, board);
};
export function zoomAt(camera: Camera, size: Size, anchor: Point, factor: number, board: Size = referenceLayout.board, topInset = 0): Camera {
  const scale = clampScale(camera.scale * factor, size, board);
  const ratio = scale / camera.scale;
  return clampCamera({ scale, x: anchor.x - (anchor.x - camera.x) * ratio,
    y: anchor.y - (anchor.y - camera.y) * ratio }, size, board, topInset);
}

/** Conservative system-font advances keep SVG description lines inside the title inset. */
const characterWidth = (character: string) => 11 * (/\s/.test(character) ? .34 : /[ilI.,'!:;|]/.test(character) ? .34
  : /[MWmw@%]/.test(character) ? 1 : /[A-Z]/.test(character) ? .8 : character.charCodeAt(0) > 255 ? 1 : .64);
export function descriptionLines(text = '', width = 148): string[] {
  const lines: string[] = [];
  if (!text.trim()) return lines;
  text.trim().split(/\r?\n/).forEach(paragraph => {
    let line = '', used = 0;
    paragraph.trim().split(/\s+/).filter(Boolean).forEach(word => {
      const wordWidth = Array.from(word).reduce((sum, char) => sum + characterWidth(char), 0);
      if (line && used + characterWidth(' ') + wordWidth > width) { lines.push(line); line = ''; used = 0; }
      if (line) { line += ' '; used += characterWidth(' '); }
      Array.from(word).forEach(char => {
        const advance = characterWidth(char);
        if (line && used + advance > width) { lines.push(line); line = ''; used = 0; }
        line += char; used += advance;
      });
    });
    lines.push(line);
  });
  return lines;
}
// Printed board values; temporary effects are resolved by the server's MartialLawHelper.
export const MARTIAL_LAW_MODIFIERS: Readonly<Record<string, number>> = {
  'Factory District': 0, 'Church Green': 0, 'Sawbone Alley': 0, "Joe Sargent's Bus Service": 0,
  'Marsh Refinery': -1, 'Innsmouth Jail': -1, 'Esoteric Order of Dagon': -1,
  'First National Grocery': 1, 'Gilman House Hotel': 1, 'Innsmouth Shore': 1,
};
export const martialLawModifier = (name: string, declared = false): number | undefined =>
  declared && (manifest.positions as Record<string, MapPosition>)[name]?.city === 'Innsmouth' ? MARTIAL_LAW_MODIFIERS[name] : undefined;
export const evadeLabel = (modifier: number) => `Evade (${modifier < 0 ? '−' : '+'}${Math.abs(modifier)})`;

function nodeLayoutBase(name: string, specialText = '', hasMonsters = false, martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout) {
  const position = geometry.positions[name];
  const jail = name.includes('Jail Cell');
  const square = ['Outskirts', 'Sky', 'Lost in Time and Space'].includes(name);
  const rectangular = position.kind === 'street' || jail || square;
  const textFrame = rectangular && !square;
  // Calibration includes the solid backing rim and its five-unit stroke.
  const frameWidth = position.frameSize ? position.frameSize.width * (square ? 1.2 : 1) - (rectangular ? 5 : 0) : 176;
  const frameHeight = square ? frameWidth : position.frameSize ? position.frameSize.height - (rectangular ? 5 : 0) : 54;
  const radiusX = square ? frameWidth / 2 : (frameWidth - 15) / 2, radiusY = (frameHeight - 15) / 2;
  const radius = position.kind === 'world' ? radiusY : radiusX;
  const titleWidth = position.label?.width ?? Math.max(164, frameWidth + (rectangular || position.kind === 'world' ? 0 : 32));
  const fontSize = textFrame && !jail ? 24 : 12;
  const names = labelLines(name, position.kind === 'world' ? undefined : rectangular ? frameWidth : titleWidth, fontSize), description = descriptionLines(specialText, titleWidth - 16);
  const nameFontSize = jail ? Math.min(13, (frameWidth - 16) / (Math.max(...names.map(line => line.length)) * .75)) : fontSize;
  const centeredMonsters = ['Outskirts', 'Sky'].includes(name);
  const monsterSize = geometry.tokens.monster;
  const monsterTop = centeredMonsters ? -monsterSize / 2 : (rectangular ? frameHeight / 2 : radius) - monsterSize / 2;
  const titleY = Math.max(rectangular ? frameHeight / 2 + 10 : radius + (position.kind === 'location' ? -6 : 10),
    hasMonsters ? monsterTop + monsterSize + 6 : 0) + (position.label?.offsetY ?? 0);
  const descriptionY = textFrame ? 14 : 16 + (names.length - 1) * 15 + 14;
  const titleHeight = description.length ? descriptionY + (description.length - 1) * 14 + 6 : textFrame ? 0 : 12 + names.length * 15;
  const titleOffsetX = position.label?.offsetX ?? 0;
  const title = { x: position.x + titleOffsetX - titleWidth / 2, y: position.y + titleY, width: titleWidth, height: titleHeight };
  const modifier = martialLawModifier(name, martialLawDeclared);
  const evade = modifier === undefined ? undefined : { x: position.x - 55,
    y: position.y + (titleHeight ? titleY + titleHeight : frameHeight / 2) + 4, width: 110, height: 20 };
  const halfWidth = rectangular ? frameWidth / 2 + 2.5 : radiusX + 7.5;
  const halfHeight = rectangular ? frameHeight / 2 + 2.5 : radius + 7.5;
  // Include the badge's half-unit stroke and the frame's outer rim in tangency.
  const encounterRadius = DIGITAL_TOKENS.encounter / 2 + .5;
  const encounterDistance = halfWidth + encounterRadius;
  // Keep two units above the normal title's stroke, independently of rules or monsters.
  const encounterY = Math.min(encounterDistance * Math.sin(Math.PI / 6), radius - 6 - 1 - encounterRadius - 2);
  const encounterOffset = Math.sqrt(encounterDistance ** 2 - encounterY ** 2 - 2000);
  const actionOffset = { x: (rectangular ? frameWidth / 2 : radius) + 4, y: position.extrasSide === 'right' ? -32 : -14 };
  return { rectangular, square, jail, frameWidth, frameHeight, radius, radiusX: rectangular ? radius : radiusX,
    radiusY: rectangular ? radius : radius, areaOffset: position.kind === 'world' ? radiusX / 2 : 0,
    halfWidth, halfHeight, names, nameFontSize, encounterOffset, encounterY,
    description, titleY, descriptionY, titleHeight, titleOffsetX, title, modifier, evade, monsterTop, actionOffset, tokens: geometry.tokens };
}
/** Global hit/camera geometry uses the same city transform as the rendered local layers. */
export function nodeLayout(name: string, specialText = '', hasMonsters = false, martialLawDeclared = false,
  geometry: DigitalMapLayout = referenceLayout): ReturnType<typeof nodeLayoutBase> {
  const position = geometry.positions[name], scale = position.renderScale ?? 1;
  if (scale === 1) return nodeLayoutBase(name, specialText, hasMonsters, martialLawDeclared, geometry);
  const local = { ...position, x: 0, y: 0, renderScale: 1,
    frameSize: position.frameSize && { width: position.frameSize.width / scale, height: position.frameSize.height / scale } };
  const base = nodeLayoutBase(name, specialText, hasMonsters, martialLawDeclared,
    { ...geometry, positions: { ...geometry.positions, [name]: local } });
  const result = Object.fromEntries(Object.entries(base).map(([key, value]) =>
    [key, typeof value === 'number' && key !== 'modifier' ? value * scale : value])) as ReturnType<typeof nodeLayoutBase>;
  const bounds = (box: Bounds): Bounds => ({ x: position.x + box.x * scale, y: position.y + box.y * scale,
    width: box.width * scale, height: box.height * scale });
  result.title = bounds(base.title);
  result.evade = base.evade ? bounds(base.evade) : undefined;
  result.actionOffset = { x: base.actionOffset.x * scale, y: base.actionOffset.y * scale };
  result.tokens = Object.fromEntries(Object.entries(base.tokens).map(([key, value]) => [key, value * scale])) as typeof DIGITAL_TOKENS;
  return result;
}

export function nodeObstacles(name: string, specialText = '', hasMonsters = false, martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout): Bounds[] {
  const p = geometry.positions[name], layout = nodeLayout(name, specialText, hasMonsters, martialLawDeclared, geometry);
  const scale = p.renderScale ?? 1, rim = 7.5 * scale;
  const artwork = layout.rectangular ? { x: p.x - layout.frameWidth / 2 - 2.5 * scale, y: p.y - layout.frameHeight / 2 - 2.5 * scale, width: layout.frameWidth + 5 * scale, height: layout.frameHeight + 5 * scale }
    : { x: p.x - layout.radiusX - rim, y: p.y - layout.radius - 12 * scale, width: layout.radiusX * 2 + rim * 2, height: layout.radius * 2 + 12 * scale + rim };
  return [artwork, ...(layout.titleHeight ? [layout.title] : []), ...(layout.evade ? [layout.evade] : [])];
}
export function nodeBounds(name: string, specialText = '', hasMonsters = false, martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout): Bounds {
  const boxes = nodeObstacles(name, specialText, hasMonsters, martialLawDeclared, geometry);
  const x = Math.min(...boxes.map(box => box.x)), y = Math.min(...boxes.map(box => box.y));
  return { x, y, width: Math.max(...boxes.map(box => box.x + box.width)) - x,
    height: Math.max(...boxes.map(box => box.y + box.height)) - y };
}
export function activityMarkerBounds(name: string, count: number, geometry: DigitalMapLayout = referenceLayout, area = 0): Bounds {
  const { board } = geometry;
  const position = geometry.positions[name], layout = nodeLayout(name, '', false, false, geometry);
  const size = DIGITAL_TOKENS.activity, height = size + Math.max(0, count - 1) * DIGITAL_TOKENS.activityStep;
  return { x: Math.max(4, Math.min(board.width - size - 4, position.x + area * layout.areaOffset + (area ? layout.radiusX / 2 : layout.halfWidth) - size / 2)),
    y: Math.max(4, Math.min(board.height - height - 4, position.y - layout.halfHeight - size / 2)),
    width: size, height };
}

/** A visible progress token is tangent to the frame, including near the left board edge. */
export function riftProgressBounds(name: string, geometry: DigitalMapLayout = referenceLayout): Bounds {
  const p = geometry.positions[name], frame = nodeLayout(name, '', false, false, geometry);
  const size = DIGITAL_TOKENS.extra, radius = size / 2, outline = 2 * DIGITAL_TOKENS.scale;
  let x = p.x - frame.halfWidth - size - outline, y = p.y - radius;
  if (x < 4 + outline) {
    x = 4 + outline;
    const dx = p.x - x - radius;
    const dy = Math.sqrt(Math.max(0, (frame.halfWidth + radius + outline) ** 2 - dx ** 2));
    y = p.y - dy - radius;
  }
  return { x, y: Math.max(4 + outline, Math.min(geometry.board.height - size - 4 - outline, y)), width: size, height: size };
}

export type MapObstacles = Record<string, Bounds[]>;
export function mapObstacles(locations: Location[], martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout): MapObstacles {
  const texts = new Map<string, Set<string>>();
  const monsters = new Set(locations.filter(place => !!place.monsters?.length && place.type !== 'OtherWorld' && place.name !== 'Lost in Time and Space').map(place => place.name));
  locations.forEach(location => {
    const name = location.type === 'OtherWorld' ? worldName(location.name) : location.name;
    const text = location.specialEncounterText?.trim();
    if (text) {
      const unique = texts.get(name) ?? new Set<string>();
      unique.add(text); texts.set(name, unique);
    }
  });
  return Object.fromEntries(Object.keys(geometry.positions).map(name => [name, nodeObstacles(name, Array.from(texts.get(name) ?? []).join('\n'), monsters.has(name), martialLawDeclared, geometry)]));
}

/** Clamp a fixed artwork-relative shortcut anchor to the board. */
export function actionBadgeBounds(preferred: Point, board: Size = referenceLayout.board, buttonSize = 28): Bounds {
  return { x: Math.max(4, Math.min(board.width - buttonSize - 4, preferred.x)),
    y: Math.max(4, Math.min(board.height - buttonSize - 4, preferred.y)), width: buttonSize, height: buttonSize };
}

export type Road = { key: string; from: string; to: string; arrows: { from: string; color: Connection['color'] }[] };
export function buildRoads(locations: Location[]): Road[] {
  const names = new Set(locations.filter(l => l.type !== 'OtherWorld').map(l => l.name));
  const roads = new Map<string, Road>();
  locations.forEach(location => (location.connections ?? []).forEach(connection => {
    if (!names.has(location.name) || !names.has(connection.to) || location.name === connection.to) return;
    const pair = [location.name, connection.to].sort();
    const key = JSON.stringify(pair);
    const road = roads.get(key) ?? { key, from: pair[0], to: pair[1], arrows: [] };
    if (connection.color !== 'None' && !road.arrows.some(a => a.from === location.name && a.color === connection.color)) {
      road.arrows.push({ from: location.name, color: connection.color });
    }
    roads.set(key, road);
  }));
  return Array.from(roads.values());
}

type EndpointShape = { center: Point; radius: number } | { center: Point; radiusX: number; radiusY: number } | { polygon: Point[] };
const rectangleShape = (box: Bounds, padding = 0): EndpointShape => ({ polygon: [
  { x: box.x - padding, y: box.y - padding }, { x: box.x + box.width + padding, y: box.y - padding },
  { x: box.x + box.width + padding, y: box.y + box.height + padding }, { x: box.x - padding, y: box.y + box.height + padding },
] });
/** Same silhouette as the rendered frame, diamond and full title, including their strokes. */
export function locationEndpointShapes(location: Location, martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout): EndpointShape[] {
  const name = location.type === 'OtherWorld' ? worldName(location.name) : location.name, p = geometry.positions[name];
  const layout = nodeLayout(name, location.specialEncounterText, location.type !== 'OtherWorld' && name !== 'Lost in Time and Space' && !!location.monsters?.length, martialLawDeclared, geometry);
  const shapes: EndpointShape[] = layout.rectangular
    ? [rectangleShape({ x: p.x - layout.frameWidth / 2, y: p.y - layout.frameHeight / 2, width: layout.frameWidth, height: layout.frameHeight }, 2.5)]
    : location.type === 'OtherWorld' ? [{ center: p, radiusX: layout.halfWidth, radiusY: layout.halfHeight }]
      : [{ center: p, radius: layout.radius + 7.5 }];
  if (!layout.rectangular && location.type === 'Location') shapes.push({ polygon: [
    { x: p.x, y: p.y - layout.radius - 12 }, { x: p.x + 11, y: p.y - layout.radius },
    { x: p.x, y: p.y - layout.radius + 12 }, { x: p.x - 11, y: p.y - layout.radius },
  ] });
  if (layout.titleHeight) shapes.push(rectangleShape(layout.title, 1));
  if (layout.evade) shapes.push(rectangleShape(layout.evade, 1));
  return shapes;
}
const cross = (a: Point, b: Point) => a.x * b.y - a.y * b.x;
function rayExit(origin: Point, direction: Point, shapes: EndpointShape[], limit: number): number {
  const exits = [0];
  shapes.forEach(shape => {
    const hits: number[] = [];
    if ('radius' in shape) {
      const delta = { x: origin.x - shape.center.x, y: origin.y - shape.center.y };
      const projection = delta.x * direction.x + delta.y * direction.y;
      const discriminant = projection ** 2 - (delta.x ** 2 + delta.y ** 2 - shape.radius ** 2);
      if (discriminant >= 0) hits.push(-projection - Math.sqrt(discriminant), -projection + Math.sqrt(discriminant));
    } else if ('radiusX' in shape) {
      const dx = direction.x / shape.radiusX, dy = direction.y / shape.radiusY;
      const ox = (origin.x - shape.center.x) / shape.radiusX, oy = (origin.y - shape.center.y) / shape.radiusY;
      const a = dx * dx + dy * dy, b = ox * dx + oy * dy;
      const discriminant = b * b - a * (ox * ox + oy * oy - 1);
      if (discriminant >= 0) hits.push((-b - Math.sqrt(discriminant)) / a, (-b + Math.sqrt(discriminant)) / a);
    } else shape.polygon.forEach((a, i) => {
      const b = shape.polygon[(i + 1) % shape.polygon.length];
      const edge = { x: b.x - a.x, y: b.y - a.y }, delta = { x: a.x - origin.x, y: a.y - origin.y };
      const denominator = cross(direction, edge);
      if (Math.abs(denominator) < 1e-9) return;
      const distance = cross(delta, edge) / denominator, along = cross(delta, direction) / denominator;
      if (along >= 0 && along <= 1) hits.push(distance);
    });
    if (hits.length && Math.min(...hits) <= limit && Math.max(...hits) >= 0) exits.push(Math.min(limit, Math.max(...hits)));
  });
  return Math.max(...exits);
}
export function clippedConnection(from: Point, to: Point, fromShapes: EndpointShape[], toShapes: EndpointShape[]): Point[] {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (!length) return [];
  const direction = { x: (to.x - from.x) / length, y: (to.y - from.y) / length };
  const start = rayExit(from, direction, fromShapes, length) + 1.5;
  const end = length - rayExit(to, { x: -direction.x, y: -direction.y }, toShapes, length) - 1.5;
  if (end <= start) return [];
  return [start, end].map(distance => ({ x: from.x + direction.x * distance, y: from.y + direction.y * distance }));
}
/** Straight border-to-border connections; unrelated objects remain above the road layer. */
export function roadPoints(from: string, to: string, locations: Location[] = [], martialLawDeclared = false, geometry: DigitalMapLayout = referenceLayout): Point[] {
  const record = (name: string): Location => locations.find(location => location.name === name) ?? {
    name, type: geometry.positions[name].kind === 'street' ? 'Street' : geometry.positions[name].kind === 'special' ? 'Special' : 'Location',
  } as Location;
  const source = record(from), vortex = geometry.vortexes.find(v => to === `vortex:${v.source}`);
  return clippedConnection(geometry.positions[from], vortex ?? geometry.positions[to], locationEndpointShapes(source, martialLawDeclared, geometry),
    vortex ? [{ center: vortex, radius: DIGITAL_TOKENS.vortex / 2 }] : locationEndpointShapes(record(to), martialLawDeclared, geometry));
}
/** The tip meets the endpoint; scale both opposing heads together if a connection is short. */
export function roadArrow(points: Point[], forward: boolean, reciprocal: boolean) {
  const length = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
  const scale = Math.max(0, Math.min(1, (length - 2) / (16 * (reciprocal ? 2 : 1))));
  const angle = Math.atan2(points[1].y - points[0].y, points[1].x - points[0].x) + (forward ? 0 : Math.PI);
  const tip = points[forward ? 1 : 0];
  return { x: tip.x - Math.cos(angle) * 8 * scale, y: tip.y - Math.sin(angle) * 8 * scale, angle: angle * 180 / Math.PI, scale, tip };
}
/** Inset the outline so its sharp miter remains inside the logical triangle/tip. */
const arrowInset = .75, arrowSlope = 6 / 16, arrowEdgeInset = arrowInset * Math.sqrt(1 + arrowSlope ** 2);
export const ROAD_ARROW_OUTLINE = `M${-8 + arrowInset} ${-(6 - arrowSlope * arrowInset - arrowEdgeInset)} L${8 - arrowEdgeInset / arrowSlope} 0 L${-8 + arrowInset} ${6 - arrowSlope * arrowInset - arrowEdgeInset} Z`;

/** Terminate the shaft at arrow bases and taper its shoulders to the scaled heads. */
export function roadShaft(points: Point[], startArrow: boolean, endArrow: boolean) {
  const length = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
  const direction = { x: (points[1].x - points[0].x) / length, y: (points[1].y - points[0].y) / length };
  const scale = roadArrow(points, true, startArrow && endArrow).scale;
  const start = startArrow ? 16 * scale : 0, end = length - (endArrow ? 16 * scale : 0);
  const startWidth = startArrow ? 6 * scale : 6.5, endWidth = endArrow ? 6 * scale : 6.5;
  const shaftLength = end - start, taper = Math.min(4, shaftLength / 2);
  const middleWidth = shaftLength < 8 ? Math.max(startWidth, endWidth) : 6.5;
  const at = (distance: number, offset = 0): Point => ({
    x: points[0].x + direction.x * distance - direction.y * offset,
    y: points[0].y + direction.y * distance + direction.x * offset,
  });
  return { points: [at(start), at(end)], clip: [at(start, startWidth), at(start + taper, middleWidth),
    at(end - taper, middleWidth), at(end, endWidth), at(end, -endWidth), at(end - taper, -middleWidth),
    at(start + taper, -middleWidth), at(start, -startWidth)] };
}


export const pathData = (points: Point[]) => points.map((p, i) => `${i ? 'L' : 'M'}${p.x},${p.y}`).join(' ');
/** Branch waypoints use source-city coordinates, independent of enabled expansions. */
export const RAILWAY_ROUTES: Record<string, Point[]> = {
  'Train Station': [],
  "Bishop's Brook Bridge": [{ x: 709.383, y: 905 }],
  'Central Hill': [{ x: 388.063, y: 600 }],
  "Joe Sargent's Bus Service": [{ x: 930, y: -96 }, { x: 930, y: 328.467 }],
};
export const RAILWAY_CORRIDOR_X = 24;
export function railwayBranch(name: string, geometry: DigitalMapLayout) {
  const destination = geometry.positions[name];
  const city = geometry.cities.find(city => city.name === destination.city)!;
  const sourceCity = manifest.cities.find(city => city.name === destination.city)!;
  const scale = city.sourceScale ?? city.bounds.width / sourceCity.width;
  const waypoints = (RAILWAY_ROUTES[name] ?? []).map(point => ({
    x: city.bounds.x + point.x * scale, y: city.bounds.y + point.y * scale,
  }));
  const junction = { x: RAILWAY_CORRIDOR_X, y: (waypoints[0] ?? destination).y };
  return { name, junction, path: pathData([junction, ...waypoints, destination]) };
}
export function buildRailway(locations: Location[], geometry: DigitalMapLayout) {
  const branches = locations.filter(location => location.train && geometry.positions[location.name])
    .map(location => railwayBranch(location.name, geometry));
  if (branches.length < 2) return [];
  const heights = branches.map(branch => branch.junction.y);
  return [...branches, { name: 'corridor', path: pathData([
    { x: RAILWAY_CORRIDOR_X, y: Math.min(...heights) },
    { x: RAILWAY_CORRIDOR_X, y: Math.max(...heights) },
  ]) }];
}

// Immutable reference scene for standalone geometry/camera helpers. Renderers pass their own layout.
const referenceLayout = buildDigitalMapLayout({ includeDunwichCards: true, includeKingsportCards: true, includeInnsmouthInCurrentGame: true });
